import {useEffect, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {DhcpOption} from '../../Types/routerForms';
import {newLocalId} from '../../Utils/random';

interface AddDhcpOptionModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (option: DhcpOption) => void;
}

const AddDhcpOptionModal = ({show, onHide, onAdd}: AddDhcpOptionModalProps): JSX.Element => {
  const intl = useIntl();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [value, setValue] = useState('');
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show) {
      setName('');
      setCode('');
      setValue('');
      setValidated(false);
    }
  }, [show]);

  const codeNum = Number(code);
  const isCodeValid = code !== '' && Number.isInteger(codeNum) && codeNum >= 0 && codeNum <= 255;
  const isNameValid = name.trim().length > 0;
  const isValueValid = value.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isCodeValid || !isNameValid || !isValueValid) return;
    onAdd({id: newLocalId('opt'), name, code, value});
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.OPTIONS.ADD'})}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit} id="addOptionForm">
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.OPTIONS.NAME'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="option3"
              isInvalid={validated && !isNameValid}
              required
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.REQUIRED'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.OPTIONS.CODE'})}
            </Form.Label>
            <Form.Control
              type="number"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="3"
              min={0}
              max={255}
              isInvalid={validated && !isCodeValid}
              required
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.OPTIONS.CODE_HELP'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.LAN.OPTIONS.VALUE'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="192.168.1.1"
              isInvalid={validated && !isValueValid}
              required
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.REQUIRED'})}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {intl.formatMessage({id: 'COMMON.CANCEL'})}
          </Button>
          <Button variant="primary" type="submit" form="addOptionForm">
            {intl.formatMessage({id: 'COMMON.ADD'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddDhcpOptionModal;
